import React, { useCallback, useEffect, useState } from "react";
import {
  createOrganizationalHierarchy,
  createOrganizationalUnit,
  deleteHierarchy,
  deleteOrgUnit,
  getHierarchies,
  getOrganizationalUnitDetails,
  getOrganizationalUnits,
  updateOrgUnit,
} from "../../supportFunctions/graphql/HierarchiesQueries";
import {
  formatDataForTree,
  setChildrenAtNode,
  TreeNode,
} from "../../sharedComponents/tree/PrimeTree";
import OrgUnitDetails from "./OrgUnitDetails";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import AddHierarchySlider from "./AddHierarchySlider";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import { PageHeader } from "@velocity-ehs/component-library";
import HierarchiesTreeCard from "./HierarchiesTreeCard";
import AddOrgUnitSlider from "./AddOrgUnitSlider";
import { TreeExpandedEvent } from "primereact/tree";
import { Hierarchy, OrgUnit } from "./Hierarchies";
import EditHierarchySlider from "./EditHierarchySlider";
import MoveOrgUnitSlider from "./MoveOrgUnitSlider";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import VerticalTabs from "../../sharedComponents/VerticalTabs";
import { useFlags } from "launchdarkly-react-client-sdk";
import classes from "./ManageHierarchies.module.scss";
import GatewayService from "../../../services/GatewayService";

const ManageHierarchies: React.FC = () => {
  let controller: AbortController | undefined;
  const { productChangeTracking } = useFlags();
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  const [options, setOptions] = useState<Hierarchy[] | undefined>(undefined);
  const [selectedOption, setSelectedOption] = useState<Hierarchy | undefined>(
    undefined
  );
  const [selectedOrg, setSelectedOrg] = useState<TreeNode | undefined>(
    undefined
  );
  const [orgDetails, setOrgDetails] = useState<OrgUnit | undefined>(undefined);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [showAddHierarchySlider, setShowAddHierarchySlider] =
    useState<boolean>(false);
  const [showAddOrgUnitSlider, setShowAddOrgUnitSlider] =
    useState<boolean>(false);
  const [showMoveOrgUnitSlider, setShowMoveOrgUnitSlider] =
    useState<boolean>(false);
  const [hierarchySlider, setHierarchySlider] = useState<boolean>(false);
  const [nodes, setNodes] = useState<TreeNode[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
  const [isDeletingOrgUnit, setIsDeletingOrgUnit] = useState<boolean>(false);
  const [showDeleteOrgUnitModal, setShowDeleteOrgUnitModal] =
    useState<boolean>(false);
  const [showDeleteHierarchyModal, setShowDeleteHierarchyModal] =
    useState<boolean>(false);
  const [isDeletingHierarchy, setIsDeletingHierarchy] =
    useState<boolean>(false);

  const updateSelectedOption = (itemData: Hierarchy) => {
    const params = new URLSearchParams(window.location.search);
    params.set("targetId", itemData?.id);
    window.history.replaceState(null, null, "?" + params.toString());
    getRootOrg(itemData.topOrgUnit?.id ?? "");
    setSelectedOption(itemData);
    //clear nodes so skeleton shows when selection changes
    setNodes(null);
  };

  async function getHierarchyOptions() {
    let items = [];
    try {
      const results = await GatewayService.api.post(
        "",
        getHierarchies({ and: { customerId: UsersService.customer.id } })
      );
      items = results.data.data.organizationalHierarchies.items;
      setOptions(items);
      updateSelectedOption(items[0]);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
    return items;
  }

  async function getOrgUnits(filter: any, path?: string) {
    setIsLoading(true);
    try {
      const results = await GatewayService.api.post(
        "",
        getOrganizationalUnits(filter)
      );
      const nodes = results.data.data.organizationalUnits.items;
      return formatDataForTree(nodes, path);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsLoading(false);
    }
  }

  const getRootOrg = async (id: string) => {
    const filter = {
      and: {
        customerId: UsersService.customer.id,
        id,
      },
    };

    const nodes = await getOrgUnits(filter);
    if (!nodes || nodes?.length === 0) return;
    getExpandedOrgUnits(nodes, nodes[0]);
    setExpandedKeys(nodes[0].key);
    getOrgUnitDetails(nodes[0]);
  };

  const getExpandedOrgUnits = async (nodes: TreeNode[], parent: TreeNode) => {
    if (!parent) return;

    const filter = {
      and: {
        customerId: UsersService.customer.id,
        parentOrganizationalUnitId: parent.id,
      },
    };

    const children = await getOrgUnits(filter, parent.key);
    if (!children) return;
    const fullTree = setChildrenAtNode(nodes, children, parent.key);
    setNodes(fullTree);
  };

  const getOrgUnitDetails = useCallback(
    async (orgUnit: TreeNode) => {
      setSelectedOrg(orgUnit);
      setIsLoadingDetails(true);
      if (controller) controller.abort();
      //TODO: refactor for possible useRef
      // eslint-disable-next-line
      controller = new AbortController();
      try {
        const response = await GatewayService.api.post(
          "",
          getOrganizationalUnitDetails(orgUnit.id),
          { signal: controller.signal }
        );

        setOrgDetails(response.data.data.organizationalUnit);
        setIsLoadingDetails(false);
        controller = undefined;
      } catch (error: any) {
        attemptShowErrorsInSnackbar(showSnackbar, error);
        if (error.message !== "canceled") {
          setIsLoadingDetails(false);
          controller = undefined;
        }
      }
    },
    [controller]
  );

  const addHierarchy = async (name: string) => {
    try {
      const hierarchy = await GatewayService.api.post(
        "",
        createOrganizationalHierarchy(UsersService.customer.id, name)
      );
      const hierarchyId = hierarchy.data.data.createOrganizationalHierarchy.id;
      if (showSnackbar)
        showSnackbar(SnackbarType.Success, `${name} was successfully added.`);
      const options = await getHierarchyOptions();
      const selection = options?.find(
        (option: any) => option.id === hierarchyId
      );
      updateSelectedOption(selection);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setShowAddHierarchySlider(false);
    }
  };

  const addOrgUnit = async (name: string) => {
    if (!selectedOrg || !selectedOption) return;
    try {
      await GatewayService.api.post(
        "",
        createOrganizationalUnit(
          UsersService.customer.id,
          selectedOption.id,
          selectedOrg.id,
          name
        )
      );
      if (showSnackbar)
        showSnackbar(
          SnackbarType.Success,
          `${name} ${t("hierarchies_ManageHierarchies_was_successfully_added")}`
        ); //TODO: translate (Revisit Interpolated String)
      setShowAddOrgUnitSlider(false);
      if (nodes) getRootOrg(nodes[0].id);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setShowAddHierarchySlider(false);
    }
  };

  const moveOrgUnit = async (destinationId: string, name: string) => {
    if (!selectedOrg) return;
    try {
      await GatewayService.api.post(
        "",
        updateOrgUnit(selectedOrg.id, undefined, destinationId)
      );
      if (showSnackbar)
        showSnackbar(
          SnackbarType.Success,
          `${t("hierarchies_ManageHierarchies_the_org_unit")} ${
            selectedOrg.label
          } ${t("hierarchies_ManageHierarchies_has_been_moved_to")} ${name}.`
        ); //TODO: translate (Revisit Interpolated String)
      if (nodes) getRootOrg(nodes[0].id);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setShowMoveOrgUnitSlider(false);
    }
  };

  const handleDeleteOrgUnit = async () => {
    if (!orgDetails) return;
    setIsDeletingOrgUnit(true);
    try {
      await GatewayService.api?.post("", deleteOrgUnit(orgDetails.id));
      if (showSnackbar)
        showSnackbar(
          SnackbarType.Success,
          `${orgDetails.name} ${t(
            "hierarchies_OrgUnitDetails_was_successfully_deleted"
          )}`
        );
      if (nodes) getRootOrg(nodes[0].id);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsDeletingOrgUnit(false);
      setShowDeleteOrgUnitModal(false);
    }
  };

  const handleDeleteHierarchy = async () => {
    if (!selectedOption) return;
    setIsDeletingHierarchy(true);
    try {
      await GatewayService.api?.post("", deleteHierarchy(selectedOption.id));
      if (showSnackbar)
        showSnackbar(
          SnackbarType.Success,
          `${selectedOption.name} ${t(
            "hierarchies_ManageHierarchies_was_successfully_deleted"
          )}`
        );
      getHierarchyOptions();
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsDeletingHierarchy(false);
      setShowDeleteHierarchyModal(false);
    }
  };

  const handleOnToggle = (event: TreeExpandedEvent) => {
    setExpandedKeys(event.value);
  };

  const handleExpanderClick = async (event: any) => {
    if (!nodes || event.node.children) return;
    getExpandedOrgUnits(nodes, event.node);
  };

  const handleRowClick = (event: any) => {
    getOrgUnitDetails(event.node);
  };

  useEffect(() => {
    getHierarchyOptions();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageHeader
        title={t("universal_Hierarchies")}
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_Hierarchies"), href: "" },
        ]}
        primaryButton={{
          label: t("universal_Add"),
          id: "addHierarchy",
          onClick: () => setShowAddHierarchySlider(true),
        }}
      />
      <VerticalTabs
        includeChangeTracking={productChangeTracking}
        targetIdentifierType="organizational_hierarchies"
        tabs={[
          {
            content: (
              <div className={classes.hierarchiesContent}>
                <HierarchiesTreeCard
                  nodes={nodes as TreeNode[]}
                  options={options}
                  selectedOption={selectedOption}
                  updateSelectedOption={updateSelectedOption}
                  expandedKeys={expandedKeys}
                  handleRowClick={handleRowClick}
                  handleExpanderClick={handleExpanderClick}
                  handleOnToggle={handleOnToggle}
                  isLoading={isLoading}
                  selected={selectedOrg?.key}
                  openHierarchySlider={setHierarchySlider}
                  closeDeleteModal={() => setShowDeleteHierarchyModal(false)}
                  isDeleteModalOpen={showDeleteHierarchyModal}
                  isDeleting={isDeletingHierarchy}
                  onDeleteHierarchy={handleDeleteHierarchy}
                  openDeleteModal={() => setShowDeleteHierarchyModal(true)}
                />
                <OrgUnitDetails
                  isLoading={isLoadingDetails}
                  onAddOrgUnit={() => setShowAddOrgUnitSlider(true)}
                  onMoveOrgUnit={() => setShowMoveOrgUnitSlider(true)}
                  orgUnit={orgDetails}
                  isDeleting={isDeletingOrgUnit}
                  onDeleteOrgUnit={handleDeleteOrgUnit}
                  closeDeleteModal={() => setShowDeleteOrgUnitModal(false)}
                  openDeleteModal={() => setShowDeleteOrgUnitModal(true)}
                  isDeleteModalOpen={showDeleteOrgUnitModal}
                />
              </div>
            ),
            title: t("universal_Hierarchies"),
          },
        ]}
      />
      <AddHierarchySlider
        closeSliderCallback={() => setShowAddHierarchySlider(false)}
        addHierarchy={addHierarchy}
        showSlider={showAddHierarchySlider}
      />
      <AddOrgUnitSlider
        closeSliderCallback={() => setShowAddOrgUnitSlider(false)}
        addOrgUnit={addOrgUnit}
        showSlider={showAddOrgUnitSlider}
      />
      <MoveOrgUnitSlider
        closeSliderCallback={() => setShowMoveOrgUnitSlider(false)}
        selectedOrg={orgDetails}
        selectedHierarchy={selectedOption}
        moveOrgUnit={moveOrgUnit}
        showSlider={showMoveOrgUnitSlider}
      />
      <EditHierarchySlider
        showSlider={hierarchySlider}
        closeSliderCallback={() => setHierarchySlider(false)}
        hierarchy={selectedOption}
        onUpdate={setSelectedOption}
        setOptions={setOptions}
      />
    </>
  );
};

export default ManageHierarchies;
